"use client";

import { Capacitor } from "@capacitor/core";
import useEmblaCarousel from "embla-carousel-react";
import {
  ChevronLeft,
  Download,
  Heart,
  Link2,
  MessageCircle,
  MoreHorizontal,
  Music2,
  Repeat2,
  Send,
  Share,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ZoomableImage } from "@/components/chat/media-viewer/chat-media-viewer";
import { PlayingBars } from "@/components/music/playing-bars";
import { ProfileLink } from "@/components/profile/profile-link";
import { useVideoAutoplay } from "@/components/providers/video-autoplay-provider";
import {
  GLASS_GROUP,
  GLASS_GROUP_DIVIDE,
  GLASS_ICON,
  GLASS_ICON_TONE,
  GLASS_MUTED_TEXT,
  GLASS_ROW,
  GLASS_SHEET,
  GLASS_SHEET_OVERLAY,
} from "@/components/share/liquid-glass-sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import type { MusicAttachment } from "@/lib/music/attachment";
import { safePreviewUrl } from "@/lib/music/links";
import { openInstantNativeSettings } from "@/lib/native/instants";
import {
  MediaSaveError,
  type SavableMedia,
  saveMediaToDevice,
  shareMediaFile,
} from "@/lib/native/save-media";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";
import {
  PLAYBACK_RATES,
  ViewerVideoControls,
} from "./viewer-video-controls.client";
import { type SpeedBoost, ViewerVideoSlide } from "./viewer-video-slide.client";

export type FeedMediaViewerItem = {
  key: string;
  kind: "image" | "video";
  src: string;
  hlsSrc?: string;
  posterSrc?: string | null;
  blurHash?: string | null;
  /**
   * A progressive file for Save and Share. Processed video plays from an
   * HLS playlist, which is not a file anyone can save; without this the
   * video can only be shared as a link.
   */
  downloadSrc?: string | null;
};

/** The post the media belongs to: X keeps its author and actions on screen. */
export type FeedMediaViewerPost = {
  author: {
    displayName: string;
    handle?: string | null;
    avatarUrl?: string | null;
  };
  caption?: string | null;
  /** The post's song: it plays over the photos, never over a video. */
  music?: MusicAttachment | null;
  likeCount: number;
  commentCount: number;
  repostCount?: number;
  isLiked: boolean;
  isReposted?: boolean;
  onLike: () => void;
  onComment: () => void;
  /** Omitted where the post can't be reposted. */
  onRepost?: () => void;
  /** Opens Hiive's share sheet (chats, the system sheet, copy, story). */
  onShare: () => void;
  likeDisabled?: boolean;
  repostDisabled?: boolean;
  shareDisabled?: boolean;
};

type FeedMediaViewerProps = {
  items: FeedMediaViewerItem[];
  initialIndex: number;
  onClose: () => void;
  post: FeedMediaViewerPost;
  /** The post's public link, for "Copy link". */
  shareUrl: string;
  /** File name stem for saved media, e.g. `hiive-post-12`. */
  fileBaseName: string;
  /** Media sent to another app, or the link copied. */
  onShared?: (target: "native" | "copy") => void;
};

/** The sheet sits over the viewer (z-110) and under toasts. */
const MENU_LAYER = 1200;

const isPlaylistUrl = (url: string) => /\.m3u8(?:[?#]|$)/i.test(url);

const formatCount = (value: number | undefined) => {
  if (!value || value <= 0) return "";
  if (value > 999_999)
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (value > 999) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return value.toLocaleString();
};

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase();

/** X's round, dark, blurred buttons over the media. */
const CHROME_BUTTON =
  "flex size-10 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-md backdrop-saturate-150 transition-transform duration-150 ease-out-strong active:scale-90";

const ACTION_PILL =
  "flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-full bg-white/[0.12] px-4 text-[14px] font-medium text-white tabular-nums transition-[transform,background-color] duration-150 ease-out-strong active:scale-95 active:bg-white/20 disabled:opacity-45";

/**
 * Fullscreen feed media, after X: back and "more" on top; the author, caption,
 * actions and (for video) the player bar at the bottom. A tap hides the
 * chrome. On video, press and hold for 2x and slide down while holding to
 * lock it. "More" saves the media to Photos (a download on the web) or sends
 * the file itself to another app.
 */
export function FeedMediaViewer({
  items,
  initialIndex,
  onClose,
  post,
  shareUrl,
  fileBaseName,
  onShared,
}: FeedMediaViewerProps) {
  const isZoomedRef = useRef(false);
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const selectedIndexRef = useRef(initialIndex);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [rateIndex, setRateIndex] = useState(0);
  const [boost, setBoost] = useState<SpeedBoost>("off");
  const [activeVideo, setActiveVideo] = useState<HTMLVideoElement | null>(null);
  const videosRef = useRef(new Map<number, HTMLVideoElement>());
  const songRef = useRef<HTMLAudioElement | null>(null);
  const [songPlaying, setSongPlaying] = useState(false);
  const { setManualPause } = useVideoAutoplay();
  const isNative = Capacitor.isNativePlatform();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: initialIndex,
    loop: false,
    watchDrag: () => !isZoomedRef.current,
  });

  useEffect(() => {
    if (!emblaApi) return;
    const handleSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      selectedIndexRef.current = index;
      setSelectedIndex(index);
      setBoost("off");
      setActiveVideo(videosRef.current.get(index) ?? null);
    };
    emblaApi.on("select", handleSelect);
    return () => {
      emblaApi.off("select", handleSelect);
    };
  }, [emblaApi]);

  const registerVideo = useCallback(
    (index: number, video: HTMLVideoElement | null) => {
      if (video) videosRef.current.set(index, video);
      else videosRef.current.delete(index);
      if (index === selectedIndexRef.current) setActiveVideo(video);
    },
    [],
  );

  // The feed card behind keeps playing otherwise, and its sound with it.
  useEffect(() => {
    setManualPause(true);
    return () => setManualPause(false);
  }, [setManualPause]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const rate = boost === "off" ? PLAYBACK_RATES[rateIndex] : 2;
  useEffect(() => {
    if (!activeVideo) return;
    // `defaultPlaybackRate` too: a source (re)load resets to it.
    activeVideo.defaultPlaybackRate = rate;
    activeVideo.playbackRate = rate;
  }, [activeVideo, rate]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (menuOpen) return;
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") emblaApi?.scrollPrev();
      if (event.key === "ArrowRight") emblaApi?.scrollNext();
      if (event.key === " " && activeVideo) {
        event.preventDefault();
        if (activeVideo.paused) void activeVideo.play().catch(() => {});
        else activeVideo.pause();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeVideo, emblaApi, menuOpen, onClose]);

  const current = items[selectedIndex];
  const songSrc = post.music ? safePreviewUrl(post.music.previewUrl) : null;
  // The feed's own track is paused with everything else (setManualPause), so
  // the viewer plays the song itself: over photos, handing off to a video.
  const songActive = Boolean(songSrc) && current?.kind === "image";

  // biome-ignore lint/correctness/useExhaustiveDependencies: `songActive` is the only trigger; `muted` is applied in `toggleMute`
  useEffect(() => {
    const song = songRef.current;
    if (!song) return;
    if (!songActive) {
      song.pause();
      return;
    }
    song.muted = muted;
    void song.play().catch(() => {
      if (song.muted) return;
      // Sound needs a gesture this open did not carry: start muted.
      song.muted = true;
      setMuted(true);
      void song.play().catch(() => {});
    });
  }, [songActive]);

  /**
   * Applied to the elements inside the tap itself. WebKit pauses media that
   * is unmuted outside a user gesture, so an effect after the re-render would
   * silence the video instead of un-muting it.
   */
  const toggleMute = () => {
    const next = !muted;
    const targets = [activeVideo, songActive ? songRef.current : null];
    for (const media of targets) {
      if (!media) continue;
      media.muted = next;
      if (!next && media.paused) void media.play().catch(() => {});
    }
    setMuted(next);
  };

  // A playlist is not a file: such a video has nothing to save until the
  // pipeline writes it a progressive copy (`downloadSrc`).
  const fileUrl =
    current?.kind === "video"
      ? (current.downloadSrc ??
        (isPlaylistUrl(current.src) ? null : current.src))
      : current?.src;
  const savable: SavableMedia | null =
    current && fileUrl
      ? {
          url: fileUrl,
          kind: current.kind,
          baseName:
            items.length > 1
              ? `${fileBaseName}-${selectedIndex + 1}`
              : fileBaseName,
        }
      : null;
  const mediaNoun = current?.kind === "video" ? "video" : "photo";

  const toggleChrome = useCallback(() => setChromeVisible((v) => !v), []);

  const handleSave = async () => {
    if (!savable) return;
    setMenuOpen(false);
    const toastId = toast.loading(
      isNative ? `Saving ${mediaNoun}…` : `Downloading ${mediaNoun}…`,
    );
    try {
      const outcome = await saveMediaToDevice(savable);
      if (outcome === "photos") {
        uiHaptics.success();
        toast.success("Saved to Photos", { id: toastId });
      } else if (outcome === "downloaded") {
        toast.success("Download started", { id: toastId });
      } else {
        toast.dismiss(toastId);
      }
    } catch (error) {
      uiHaptics.error();
      if (error instanceof MediaSaveError && error.reason === "permission") {
        toast.error("Allow Hiive to add to Photos", {
          id: toastId,
          action: {
            label: "Settings",
            onClick: () => void openInstantNativeSettings(),
          },
        });
        return;
      }
      toast.error(
        error instanceof Error ? error.message : `Couldn't save ${mediaNoun}`,
        { id: toastId },
      );
    }
  };

  const handleShareFile = async () => {
    if (!savable) return;
    setMenuOpen(false);
    try {
      const result = await shareMediaFile(savable);
      if (result.ok) onShared?.("native");
      else if (result.reason !== "aborted")
        toast.error("Sharing isn't available here");
    } catch {
      toast.error(`Couldn't share ${mediaNoun}`);
    }
  };

  const handleCopyLink = async () => {
    setMenuOpen(false);
    try {
      await navigator.clipboard.writeText(shareUrl);
      onShared?.("copy");
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const chromeClass = cn(
    "transition-opacity duration-200 ease-out-strong",
    chromeVisible ? "opacity-100" : "pointer-events-none opacity-0",
  );
  const { author } = post;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${author.displayName}'s ${mediaNoun}`}
      className="fixed inset-0 z-[110] flex flex-col bg-black"
    >
      {songSrc ? (
        // biome-ignore lint/a11y/useMediaCaption: a song preview has no captions to offer
        <audio
          ref={songRef}
          src={songSrc}
          loop
          preload="auto"
          onPlay={() => setSongPlaying(true)}
          onPause={() => setSongPlaying(false)}
        />
      ) : null}
      <div ref={emblaRef} className="h-full overflow-hidden">
        <div className="flex h-full">
          {items.map((item, index) => (
            <div key={item.key} className="h-full min-w-0 flex-[0_0_100%]">
              {item.kind === "image" ? (
                <ZoomableImage
                  src={item.src}
                  alt={`Photo by ${author.displayName}`}
                  onRequestClose={onClose}
                  onTap={toggleChrome}
                  onZoomChange={(zoomed) => {
                    if (index === selectedIndex) isZoomedRef.current = zoomed;
                  }}
                />
              ) : Math.abs(index - selectedIndex) <= 1 ? (
                // Only the active slide and its neighbours hold a player.
                <ViewerVideoSlide
                  index={index}
                  src={item.src}
                  hlsSrc={item.hlsSrc}
                  posterSrc={item.posterSrc}
                  blurHash={item.blurHash}
                  active={index === selectedIndex}
                  muted={muted}
                  boost={index === selectedIndex ? boost : "off"}
                  registerVideo={registerVideo}
                  onBoostChange={setBoost}
                  onAutoplayMuted={() => setMuted(true)}
                  onTap={toggleChrome}
                  onRequestClose={onClose}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-black/55 to-transparent pt-[env(safe-area-inset-top)]",
          chromeClass,
        )}
      >
        <div className="pointer-events-auto flex items-center justify-between px-3 py-2">
          <button
            type="button"
            onClick={onClose}
            className={CHROME_BUTTON}
            aria-label="Close"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            type="button"
            onClick={() => {
              uiHaptics.tap();
              setMenuOpen(true);
            }}
            className={CHROME_BUTTON}
            aria-label="More options"
          >
            <MoreHorizontal className="size-5" />
          </button>
        </div>
      </div>

      <div
        className={cn(
          // Dense enough to read over a busy, full-height photo.
          "pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black from-40% via-black/80 to-transparent pt-24 pb-[max(env(safe-area-inset-bottom),0.75rem)]",
          chromeClass,
        )}
      >
        <div className="pointer-events-auto mx-auto flex w-full max-w-2xl flex-col gap-3 px-4">
          {items.length > 1 ? (
            <div
              className="flex justify-center gap-1.5"
              role="tablist"
              aria-label="Media"
            >
              {items.map((item, index) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={index === selectedIndex}
                  aria-label={`Item ${index + 1} of ${items.length}`}
                  onClick={() => emblaApi?.scrollTo(index)}
                  className={cn(
                    "size-1.5 rounded-full transition-colors duration-200",
                    index === selectedIndex ? "bg-white" : "bg-white/35",
                  )}
                />
              ))}
            </div>
          ) : null}

          <div className="flex min-w-0 items-center gap-2.5">
            <ProfileLink
              handle={author.handle}
              fallbackAs="div"
              onClick={onClose}
              className="flex min-w-0 items-center gap-2.5"
            >
              <Avatar className="size-9 shrink-0">
                <AvatarImage
                  src={author.avatarUrl ?? undefined}
                  alt={author.displayName}
                />
                <AvatarFallback className="rounded-none text-[11px]">
                  {getInitials(author.displayName)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 leading-tight">
                <div className="truncate text-[15px] font-semibold text-white">
                  {author.displayName}
                </div>
                {author.handle ? (
                  <div className="truncate text-[13px] text-white/60">
                    @{author.handle}
                  </div>
                ) : null}
              </div>
            </ProfileLink>
          </div>

          {post.music ? (
            <div className="-my-1 flex min-w-0 items-center gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-white">
                {/* Bars while the song is audible, a note otherwise: the
                    same cue the feed's song credit uses. */}
                <span className="flex size-3.5 shrink-0 items-center justify-center">
                  {songActive && songPlaying && !muted ? (
                    <PlayingBars className="h-3" barClassName="w-[2px]" />
                  ) : (
                    <Music2 className="size-3.5" aria-hidden="true" />
                  )}
                </span>
                <span className="truncate">
                  {post.music.title}
                  <span className="text-white/60">
                    {" "}
                    · {post.music.artistName}
                  </span>
                </span>
              </div>
              {songActive ? (
                <button
                  type="button"
                  onClick={toggleMute}
                  className="-mr-2 flex size-10 shrink-0 items-center justify-center rounded-full text-white transition-transform duration-150 ease-out-strong active:scale-90"
                  aria-label={muted ? "Turn song on" : "Turn song off"}
                  aria-pressed={!muted}
                >
                  {muted ? (
                    <VolumeX className="size-[21px]" />
                  ) : (
                    <Volume2 className="size-[21px]" />
                  )}
                </button>
              ) : null}
            </div>
          ) : null}

          {post.caption ? (
            <button
              type="button"
              onClick={() => setCaptionExpanded((v) => !v)}
              aria-expanded={captionExpanded}
              className={cn(
                "block w-full text-left text-[15px] leading-5 whitespace-pre-line text-white",
                captionExpanded &&
                  "max-h-[35vh] overflow-y-auto overscroll-contain",
              )}
            >
              {/* The clamp lives on a block inside: WebKit ignores
                  -webkit-box (line-clamp) on a <button> itself. */}
              <span className={cn("block", !captionExpanded && "line-clamp-2")}>
                {post.caption}
              </span>
            </button>
          ) : null}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={post.onComment}
              className={ACTION_PILL}
              aria-label="Comment"
            >
              <MessageCircle className="size-[18px]" strokeWidth={1.9} />
              {formatCount(post.commentCount)}
            </button>
            {post.onRepost ? (
              <button
                type="button"
                onClick={post.onRepost}
                disabled={post.repostDisabled}
                className={cn(ACTION_PILL, post.isReposted && "text-success")}
                aria-label="Repost"
                aria-pressed={post.isReposted}
              >
                <Repeat2 className="size-5" strokeWidth={1.9} />
                {formatCount(post.repostCount)}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                uiHaptics.like();
                post.onLike();
              }}
              disabled={post.likeDisabled}
              className={cn(ACTION_PILL, post.isLiked && "text-error")}
              aria-label={post.isLiked ? "Unlike" : "Like"}
              aria-pressed={post.isLiked}
            >
              <Heart
                className={cn("size-[18px]", post.isLiked && "fill-error")}
                strokeWidth={1.9}
              />
              {formatCount(post.likeCount)}
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={post.onShare}
              disabled={post.shareDisabled}
              className={cn(ACTION_PILL, "px-3")}
              aria-label="Share"
            >
              <Send className="size-[17px]" strokeWidth={1.9} />
            </button>
          </div>

          {current?.kind === "video" ? (
            <ViewerVideoControls
              video={activeVideo}
              rate={rate}
              muted={muted}
              onCycleRate={() => {
                uiHaptics.select();
                setBoost("off");
                setRateIndex((i) => (i + 1) % PLAYBACK_RATES.length);
              }}
              onToggleMute={toggleMute}
            />
          ) : null}
        </div>
      </div>

      <Drawer open={menuOpen} onOpenChange={setMenuOpen}>
        <DrawerContent
          layer={MENU_LAYER}
          className={GLASS_SHEET}
          overlayClassName={GLASS_SHEET_OVERLAY}
        >
          <DrawerTitle className="sr-only">Media options</DrawerTitle>
          <div className="px-4 pt-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            <div className={cn(GLASS_GROUP, GLASS_GROUP_DIVIDE)}>
              <button
                type="button"
                className={GLASS_ROW}
                disabled={!savable}
                onClick={() => void handleSave()}
              >
                <span className={cn(GLASS_ICON, GLASS_ICON_TONE.neutral)}>
                  <Download />
                </span>
                <span className="flex min-w-0 flex-col">
                  {isNative ? `Save ${mediaNoun}` : `Download ${mediaNoun}`}
                  {savable ? null : (
                    <span className={cn("text-[12px]", GLASS_MUTED_TEXT)}>
                      Not available for this video yet
                    </span>
                  )}
                </span>
              </button>
              <button
                type="button"
                className={GLASS_ROW}
                disabled={!savable}
                onClick={() => void handleShareFile()}
              >
                <span className={cn(GLASS_ICON, GLASS_ICON_TONE.neutral)}>
                  <Share />
                </span>
                Share {mediaNoun} to…
              </button>
              <button
                type="button"
                className={GLASS_ROW}
                onClick={() => void handleCopyLink()}
              >
                <span className={cn(GLASS_ICON, GLASS_ICON_TONE.neutral)}>
                  <Link2 />
                </span>
                Copy link
              </button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>,
    document.body,
  );
}
